import { useEffect, useState } from "react";
import { CalendarDays, TrendingUp, TrendingDown, Scale, Printer, FileSpreadsheet, FileText,
         ReceiptText, AlertTriangle, Wallet, Boxes } from "lucide-react";
import { api, descargarExcel, descargarPdf } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Loading, PageTitle, Stat } from "@/components/Common";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CobroFactura } from "@/components/CobroFactura";
import { CierreFirmado } from "@/components/CierreFirmado";
import { usePermisos } from "@/lib/permisos";
import { useAuth } from "@/context/AuthContext";

const Tabla = ({ titulo, icono: Icono, testId, columnas, filas, vacio }) => (
  <section className="mt-8">
    <div className="flex items-center gap-2">
      {Icono && <Icono className="h-4 w-4 text-primary" />}
      <h2 className="font-display text-lg font-semibold">{titulo}</h2>
    </div>
    <div className="mt-4 overflow-x-auto rounded-lg border border-border/60 bg-card">
      <table className="w-full min-w-[640px] text-sm" data-testid={testId}>
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
          <tr>{columnas.map((c) => <th key={c.label} className="px-6 py-3">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={`${f.id || "fila"}-${i}`} className="border-t border-border/60">
              {columnas.map((c) => <td key={c.label} className="px-6 py-4">{c.render(f)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {filas.length === 0 && (
        <p className="p-8 text-center text-sm text-muted-foreground" data-testid={`${testId}-vacio`}>{vacio}</p>
      )}
    </div>
  </section>
);

const Mini = ({ label, valor, hint, tono = "", testId }) => (
  <div data-testid={testId}>
    <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`mt-1 font-display text-xl font-bold ${tono}`}>{valor}</p>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function AdminCaja() {
  const [dia, setDia] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [cobrar, setCobrar] = useState(null);
  const { puede } = usePermisos();
  const { user } = useAuth();
  const puedeCobrar = puede("facturas:cobrar");

  const cargar = () => api.get("/reportes/cierre-caja", { params: { fecha: dia } })
    .then((r) => setData(r.data));

  useEffect(() => {
    setData(null);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dia]);

  const botonCobrar = (f) => (
    puedeCobrar && f.saldo > 0 ? (
      <Button size="sm" className="rounded-full text-xs" data-testid={`cobrar-${f.ncf}`}
              onClick={() => setCobrar(f)}>
        <Wallet className="mr-1 h-3 w-3" /> Cobrar
      </Button>
    ) : null
  );

  const fac = data?.facturacion_dia;
  const car = data?.cartera;
  const inv = data?.inventario_dia;

  return (
    <div data-testid="admin-caja">
      <PageTitle title="Cierre de caja diario"
                 subtitle="Facturación del día, cobros, ventas, gastos, artículos despachados y todo lo que queda por cobrar.">
        <div className="flex flex-wrap items-end gap-3">
          <Input type="date" value={dia} onChange={(e) => setDia(e.target.value)} data-testid="caja-fecha"
                 className="w-44 rounded-full" />
          <Button variant="outline" className="rounded-full"
                  onClick={() => descargarPdf("/reportes/cierre-caja/pdf", { fecha: dia },
                                              `cierre-de-caja-resumen-${dia}.pdf`)
                    .catch(() => toast.error("No se pudo generar el PDF"))}
                  data-testid="pdf-resumen-caja">
            <FileText className="mr-2 h-4 w-4" /> PDF resumen
          </Button>
          <Button variant="outline" className="rounded-full"
                  onClick={() => descargarPdf("/reportes/cierre-caja/pdf",
                                              { fecha: dia, detallado: true },
                                              `cierre-de-caja-detallado-${dia}.pdf`)
                    .catch(() => toast.error("No se pudo generar el PDF"))}
                  data-testid="pdf-detallado-caja">
            <FileText className="mr-2 h-4 w-4" /> PDF detallado
          </Button>
          <Button variant="outline" className="rounded-full"
                  onClick={() => descargarExcel("/reportes/excel", { tipo: "cierre_caja", fecha: dia },
                                                `cierre-de-caja-${dia}.xlsx`)}
                  data-testid="descargar-excel-caja">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => window.print()}
                  data-testid="imprimir-caja">
            <Printer className="mr-2 h-4 w-4" /> Imprimir
          </Button>
        </div>
      </PageTitle>

      {!data ? <Loading /> : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Stat testId="caja-ingresos" label="Ingresos por facturas" value={money(data.ingresos_facturas)}
                  tone="good" icon={TrendingUp} hint={`${data.facturas.length} factura(s) cobrada(s)`} />
            <Stat testId="caja-ventas" label="Ingresos por ventas" value={money(data.ingresos_ventas)}
                  hint={`${data.ventas.length} venta(s)`} />
            <Stat testId="caja-gastos" label="Gastos del día" value={money(data.total_gastos)}
                  tone="bad" icon={TrendingDown} hint={`${data.gastos.length} gasto(s)`} />
            <Stat testId="caja-balance" label="Balance neto" value={money(data.balance_neto)}
                  tone={data.balance_neto >= 0 ? "good" : "bad"} icon={Scale}
                  hint={`Ingresos ${money(data.total_ingresos)}`} />
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Stat testId="caja-facturado-hoy" label="Facturado hoy" value={money(fac.emitidas.monto)}
                  icon={ReceiptText} hint={`${fac.emitidas.cantidad} factura(s) · fiscal ${money(fac.fiscales.monto)} · sin valor fiscal ${money(fac.sin_valor_fiscal.monto)}`} />
            <Stat testId="caja-pendiente-hoy" label="Pendiente de lo facturado hoy"
                  value={money(fac.pendientes.monto)} tone="warn"
                  hint={`${fac.pendientes.cantidad} sin cobrar · cobrado ${money(fac.cobradas.monto)}`} />
            <Stat testId="caja-en-atraso" label="Cobros en atraso" value={money(car.en_atraso.monto)}
                  tone="bad" icon={AlertTriangle}
                  hint={`${car.en_atraso.cantidad} factura(s) · ${car.familias_en_atraso} familia(s)`} />
            <Stat testId="caja-por-cobrar" label="Total por cobrar" value={money(car.por_cobrar.monto)}
                  icon={Wallet} hint={`${car.por_cobrar.cantidad} factura(s) pendiente(s) al cierre`} />
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8" data-testid="caja-cartera">
              <h2 className="font-display text-lg font-semibold">Cartera al cierre del {fecha(dia)}</h2>
              <div className="mt-4 grid gap-6 sm:grid-cols-3">
                <Mini testId="cartera-por-vencer" label="Por vencer" valor={money(car.por_vencer.monto)}
                      hint={`${car.por_vencer.cantidad} factura(s)`} />
                <Mini testId="cartera-en-atraso" label="En atraso" valor={money(car.en_atraso.monto)}
                      tono="text-destructive" hint={`${car.en_atraso.cantidad} factura(s)`} />
                <Mini testId="cartera-por-cobrar" label="Total por cobrar" valor={money(car.por_cobrar.monto)}
                      hint={`${car.familias_en_atraso} familia(s) en atraso`} />
              </div>
            </div>

            <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8" data-testid="caja-por-metodo">
              <h2 className="font-display text-lg font-semibold">Desglose por método de cobro</h2>
              {data.por_metodo.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">Sin cobros en esta fecha.</p>
              ) : (
                <div className="mt-4 flex flex-wrap gap-6">
                  {data.por_metodo.map((m) => (
                    <Mini key={m.metodo} label={m.metodo} valor={money(m.monto)} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <Tabla titulo={`Facturación del ${fecha(dia)}`} icono={ReceiptText} testId="caja-tabla-facturacion"
                 filas={fac.items} vacio="No se emitieron facturas en esta fecha."
                 columnas={[
                   { label: "Comprobante", render: (f) => <span className="font-mono text-xs">{f.ncf}</span> },
                   { label: "Fiscal", render: (f) => <Badge variant={f.fiscal === false ? "secondary" : "outline"}>{f.valor_fiscal}</Badge> },
                   { label: "Estudiante", render: (f) => f.estudiante_nombre },
                   { label: "Concepto", render: (f) => f.concepto },
                   { label: "Vence", render: (f) => fecha(f.fecha_vencimiento) },
                   { label: "Estado", render: (f) => <Badge variant={f.estado === "pagado" ? "default" : "outline"}>{f.estado}</Badge> },
                   { label: "Monto", render: (f) => (
                     <span className="font-mono font-semibold">
                       {money(f.monto_total)}
                       {f.monto_pagado > 0 && f.saldo > 0 && (
                         <span className="mt-1 block text-[10px] font-normal text-muted-foreground">
                           abonado {money(f.monto_pagado)} · balance {money(f.saldo)}
                         </span>
                       )}
                     </span>
                   ) },
                   { label: "", render: botonCobrar },
                 ]} />

          <Tabla titulo={`Cobros del ${fecha(dia)}`} icono={CalendarDays} testId="caja-tabla-facturas"
                 filas={data.facturas} vacio="No hay cobros registrados en esta fecha."
                 columnas={[
                   { label: "NCF", render: (f) => <span className="font-mono text-xs">{f.ncf}</span> },
                   { label: "Estudiante", render: (f) => f.estudiante_nombre },
                   { label: "Concepto", render: (f) => f.concepto || f.detalle?.[0]?.concepto },
                   { label: "Tipo", render: (f) => <Badge variant={f.tipo_cobro === "Abono" ? "secondary" : "outline"}>{f.tipo_cobro}</Badge> },
                   { label: "Método", render: (f) => <Badge variant="outline">{f.metodo_pago}</Badge> },
                   { label: "Balance", render: (f) => <span className="font-mono text-xs">{money(f.saldo)}</span> },
                   { label: "Cobrado", render: (f) => <span className="font-mono font-semibold">{money(f.monto_total)}</span> },
                 ]} />

          <Tabla titulo="Cobros en atraso" icono={AlertTriangle} testId="caja-tabla-morosos"
                 filas={data.morosos} vacio="No hay facturas vencidas al cierre de esta fecha."
                 columnas={[
                   { label: "Comprobante", render: (f) => <span className="font-mono text-xs">{f.ncf}</span> },
                   { label: "Estudiante", render: (f) => f.estudiante_nombre },
                   { label: "Familia", render: (f) => f.tutor_nombre || "—" },
                   { label: "Concepto", render: (f) => f.concepto },
                   { label: "Venció", render: (f) => fecha(f.fecha_vencimiento) },
                   { label: "Atraso", render: (f) => <Badge variant="destructive">{f.dias_atraso} día(s)</Badge> },
                   { label: "Balance", render: (f) => (
                     <span className="font-mono font-semibold text-destructive">
                       {money(f.saldo)}
                       {f.monto_pagado > 0 && (
                         <span className="mt-1 block text-[10px] font-normal text-muted-foreground">
                           de {money(f.monto_total)}
                         </span>
                       )}
                     </span>
                   ) },
                   { label: "", render: botonCobrar },
                 ]} />

          <Tabla titulo={`Artículos despachados · ${inv.unidades} unidad(es) · ${money(inv.monto)}`}
                 icono={Boxes} testId="caja-tabla-inventario" filas={inv.articulos}
                 vacio="No se despachó inventario en esta fecha."
                 columnas={[
                   { label: "Código", render: (a) => <span className="font-mono text-xs">{a.codigo}</span> },
                   { label: "Artículo", render: (a) => a.articulo },
                   { label: "Unidades", render: (a) => a.cantidad },
                   { label: "Existencia al cierre", render: (a) => a.existencia },
                   { label: "Monto", render: (a) => <span className="font-mono font-semibold">{money(a.monto)}</span> },
                 ]} />

          <Tabla titulo="Gastos del día" icono={TrendingDown} testId="caja-tabla-gastos"
                 filas={data.gastos} vacio="No hay gastos registrados en esta fecha."
                 columnas={[
                   { label: "Categoría", render: (g) => g.categoria },
                   { label: "Descripción", render: (g) => g.descripcion },
                   { label: "Tipo", render: (g) => <Badge variant="outline">{g.tipo}</Badge> },
                   { label: "Comprobante", render: (g) => <span className="text-xs text-muted-foreground">{g.comprobante_nombre || "sin adjunto"}</span> },
                   { label: "Monto", render: (g) => <span className="font-mono font-semibold text-destructive">-{money(g.monto)}</span> },
                 ]} />

          <CierreFirmado dia={dia} efectivoSistema={data.efectivo_sistema} usuario={user?.nombre} />

          <CobroFactura factura={cobrar} onClose={() => setCobrar(null)} onCobrado={cargar} />
        </>
      )}
    </div>
  );
}
